import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import CallCard from "@/components/CallCard";
import AuthModal from "@/components/AuthModal";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BadgeCheck, Trophy, Star } from "lucide-react";
import { toast } from "sonner";
import NotFound from "@/pages/NotFound";

function Stat({ label, value }) {
  return (
    <div className="border-2 border-ink bg-white p-3 text-center">
      <div className="font-mono text-2xl font-bold">{value ?? "—"}</div>
      <div className="text-xs text-muted-foreground uppercase tracking-wide">{label}</div>
    </div>
  );
}

export default function Profile() {
  const { username } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [calls, setCalls] = useState([]);
  const [tab, setTab] = useState("all");
  const [sort, setSort] = useState("newest");
  const [following, setFollowing] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [notFound, setNotFound] = useState(false);

  const uname = (username || "").replace(/^@/, "");
  // Static-style paths (e.g. ads.txt, robots.txt) are never valid usernames.
  const looksLikeFile = uname.includes(".");

  const loadProfile = useCallback(() => {
    api.get(`/users/${uname}`).then(({ data }) => { setData(data); setFollowing(data.is_following); })
      .catch(() => setNotFound(true));
  }, [uname]);

  useEffect(() => { loadProfile(); }, [loadProfile]);
  useEffect(() => {
    api.get(`/users/${uname}/calls?tab=${tab}&sort=${sort}`).then(({ data }) => setCalls(data)).catch(() => {});
  }, [uname, tab, sort]);

  const toggleFollow = async () => {
    if (!user) return setAuthOpen(true);
    try {
      if (following) { await api.delete(`/users/${uname}/follow`); setFollowing(false); }
      else { await api.post(`/users/${uname}/follow`); setFollowing(true); }
      loadProfile();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  if (looksLikeFile) return <NotFound />;
  if (notFound) return <div className="max-w-lg mx-auto px-4 py-24 text-center"><p className="font-display text-4xl font-bold">User not found</p></div>;
  if (!data) return <div className="max-w-lg mx-auto px-4 py-24 text-center text-muted-foreground">Loading…</div>;

  const p = data.profile, s = data.stats;

  return (
    <div className="max-w-2xl mx-auto px-4 py-8">
      <div className="card-brutal p-6 bg-white">
        <div className="flex items-start gap-4">
          <Avatar className="w-20 h-20 border-2 border-ink">
            <AvatarImage src={p.avatar_url} />
            <AvatarFallback className="bg-locked text-2xl font-bold">{(p.display_name || "U")[0]}</AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 flex-wrap">
              <h1 className="font-display text-2xl font-bold tracking-tight">{p.display_name}</h1>
              {p.verification_status === "verified" && (
                <span data-testid="verified-badge" title="Verified identity" className="inline-flex items-center gap-0.5 text-brand text-xs font-bold border-2 border-brand px-1.5 py-0.5">
                  <BadgeCheck className="w-3.5 h-3.5" /> Verified
                </span>
              )}
            </div>
            <div className="text-muted-foreground font-mono">@{p.username}</div>
            {p.bio && <p className="text-sm mt-2">{p.bio}</p>}
          </div>
          {!data.is_self && (
            <button data-testid="follow-btn" onClick={toggleFollow}
              className={`h-10 px-4 border-2 border-ink font-bold btn-press shadow-brutal-sm ${following ? "bg-white" : "bg-brand text-white"}`}>
              {following ? "Following" : "Follow"}
            </button>
          )}
        </div>

        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mt-6">
          <Stat label="Calls" value={s.total} />
          <Stat label="Called It" value={s.called_it} />
          <Stat label="Missed" value={s.missed_it} />
          <Stat label="Pending" value={s.pending} />
          <Stat label="Accuracy" value={s.accuracy != null ? `${s.accuracy}%` : null} />
          <Stat label="Avg Conf" value={s.avg_confidence != null ? `${s.avg_confidence}%` : null} />
          <Stat label="Avg Early" value={s.avg_days_early != null ? `${s.avg_days_early}d` : null} />
          <Stat label="Followers" value={s.followers} />
        </div>
      </div>

      {data.best_calls?.length > 0 && (
        <div className="mt-6">
          <h2 className="font-display text-xl font-bold mb-3 flex items-center gap-2"><Trophy className="w-5 h-5" /> Best Calls</h2>
          <div className="space-y-4">{data.best_calls.map((c) => <CallCard key={c.short_id} call={c} />)}</div>
        </div>
      )}

      {s.category_performance?.length > 0 && (
        <div className="mt-6 card-brutal p-5 bg-white">
          <h2 className="font-display text-lg font-bold mb-3">Category performance</h2>
          <div className="space-y-2">
            {s.category_performance.map((cp) => (
              <div key={cp.category} className="flex items-center justify-between text-sm">
                <span className="font-semibold">{cp.label}</span>
                {cp.stars ? (
                  <span className="flex gap-0.5">
                    {[1, 2, 3, 4, 5].map((i) => <Star key={i} className={`w-4 h-4 ${i <= cp.stars ? "fill-locked text-ink" : "text-muted-foreground"}`} />)}
                  </span>
                ) : <span className="text-xs text-muted-foreground">{cp.note}</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-8">
        <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="border-2 border-ink bg-white">
              <TabsTrigger value="all" data-testid="tab-all">Calls</TabsTrigger>
              <TabsTrigger value="called_it" data-testid="tab-called">Called It</TabsTrigger>
              <TabsTrigger value="pending" data-testid="tab-pending">Pending</TabsTrigger>
              <TabsTrigger value="missed" data-testid="tab-missed">Missed</TabsTrigger>
            </TabsList>
          </Tabs>
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger className="border-2 border-ink w-40 h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="newest">Newest</SelectItem>
              <SelectItem value="oldest">Oldest</SelectItem>
              <SelectItem value="confident">Most confident</SelectItem>
              <SelectItem value="earliest_success">Earliest win</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-5">
          {calls.map((c) => <CallCard key={c.short_id} call={c} />)}
          {calls.length === 0 && <p className="text-center text-muted-foreground py-8">No Calls here yet.</p>}
        </div>
      </div>
      <AuthModal open={authOpen} onOpenChange={setAuthOpen} title="Sign in to follow" />
    </div>
  );
}
